<template>
  <div class="toolbar-wrapper">
    <div
      v-if="editor"
      class="tiptap-toolbar-container"
      :class="{ 'is-fixed': isToolbarFixed }"
      ref="toolbarRef"
    >
      <div class="tiptap-toolbar">
        <!-- History Buttons -->
        <button
          @click="editor.chain().focus().undo().run()"
          :disabled="!editor.can().chain().focus().undo().run()"
          title="Undo"
        >
          <img
            src="/images/icon/undo.svg"
            alt="Undo"
            style="filter: brightness(0)"
          />
        </button>
        <button
          @click="editor.chain().focus().redo().run()"
          :disabled="!editor.can().chain().focus().redo().run()"
          title="Redo"
        >
          <img src="/images/icon/redo.svg" alt="Redo" />
        </button>

        <div class="divider"></div>

        <!-- Headings Dropdown -->
        <FormatDropdown :editor="editor" />

        <!-- Format Buttons (Bold, Italic, etc.) -->
        <FormatButtons :editor="editor" />

        <!-- Alignment Buttons -->
        <AlignmentButtons :editor="editor" />

        <!-- List Buttons -->
        <ListButtons :editor="editor" />

        <!-- Link Buttons -->
        <LinkButtons :editor="editor" @open-link-modal="openLinkModal" />

        <!-- Media Buttons -->
        <MediaButtons
          :editor="editor"
          @open-image-modal="openImageModal"
          @open-youtube-modal="openYoutubeModal"
        />

        <div class="divider"></div>

        <!-- Tooltip Buttons -->
        <TooltipButtons
          :editor="editor"
          @open-tooltip-modal="openTooltipModal"
        />

        <div class="divider"></div>

        <!-- Table Buttons -->
        <TableButtons
          :editor="editor"
          @open-cell-bg-color-modal="openCellBgColorModal"
        />
      </div>
    </div>

    <!-- Placeholder element to prevent content jumping when toolbar becomes fixed -->
    <div
      v-if="isToolbarFixed"
      class="toolbar-placeholder"
      :style="{ height: `${toolbarPosition.height}px` }"
    ></div>

    <!-- Modals -->
    <ImageModal
      :editor="editor"
      :showModal="showImageModal"
      @close="closeImageModal"
    />

    <YouTubeModal
      :editor="editor"
      :showModal="showYoutubeModal"
      @close="closeYoutubeModal"
    />

    <LinkModal
      :editor="editor"
      :showModal="showLinkModal"
      @close="closeLinkModal"
    />

    <TooltipModal
      :editor="editor"
      :showModal="showTooltipModal"
      :iconType="tooltipIconType"
      @close="closeTooltipModal"
    />

    <CellBgColorModal
      :editor="editor"
      :showModal="showCellBgColorModal"
      @close="closeCellBgColorModal"
    />
  </div>
</template>

<script setup>
import {
  defineProps,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";

// Import components
import FormatButtons from "./editor/toolbar/FormatButtons.vue";
import FormatDropdown from "./editor/toolbar/FormatDropdown.vue";
import AlignmentButtons from "./editor/toolbar/AlignmentButtons.vue";
import ListButtons from "./editor/toolbar/ListButtons.vue";
import LinkButtons from "./editor/toolbar/LinkButtons.vue";
import MediaButtons from "./editor/toolbar/MediaButtons.vue";
import TooltipButtons from "./editor/toolbar/TooltipButtons.vue";
import TableButtons from "./editor/toolbar/TableButtons.vue";

// Import modals
import ImageModal from "./editor/modals/ImageModal.vue";
import YouTubeModal from "./editor/modals/YouTubeModal.vue";
import LinkModal from "./editor/modals/LinkModal.vue";
import TooltipModal from "./editor/modals/TooltipModal.vue";
import CellBgColorModal from "./editor/modals/CellBgColorModal.vue";

const props = defineProps({
  editor: {
    type: Object,
    required: true,
  },
  editorId: {
    type: String,
    default: "",
  },
});

// Modal states
const showImageModal = ref(false);
const showYoutubeModal = ref(false);
const showLinkModal = ref(false);
const showTooltipModal = ref(false);
const showCellBgColorModal = ref(false);
const tooltipIconType = ref("hammer");

// Scroll position tracking
const toolbarRef = ref(null);
const isToolbarFixed = ref(false);
const toolbarPosition = ref({ top: 0, originalTop: 0, height: 0 });

// Add debounced scroll handler for better performance
let scrollTimeout;
const handleScroll = () => {
  if (scrollTimeout) {
    window.cancelAnimationFrame(scrollTimeout);
  }

  scrollTimeout = window.requestAnimationFrame(() => {
    updateToolbarPosition();
  });
};

// Separate the position calculation logic
const updateToolbarPosition = () => {
  if (!toolbarRef.value) return;

  const scrollY = window.scrollY;
  const toolbar = toolbarRef.value;
  const parent = toolbar.closest(".section");

  if (!parent) return;

  // Get parent bounds
  const parentRect = parent.getBoundingClientRect();
  const parentBottom = parentRect.bottom + window.scrollY;

  // Get toolbar dimensions if they haven't been initialized yet
  if (!toolbarPosition.value.height || !toolbarPosition.value.originalTop) {
    const rect = toolbar.getBoundingClientRect();
    toolbarPosition.value = {
      top: rect.top + window.scrollY,
      originalTop: rect.top + window.scrollY, // Store the original position
      height: rect.height,
    };
  }

  // Get the original position of the toolbar in the document
  const originalPosition = toolbarPosition.value.originalTop;

  // Determine if toolbar should be fixed based on scroll position relative to original position
  if (scrollY > originalPosition) {
    // Only when scrolling below the original position AND still within the parent section
    if (
      !isToolbarFixed.value &&
      scrollY < parentBottom - toolbarPosition.value.height
    ) {
      isToolbarFixed.value = true;
      toolbar.style.width = `${parentRect.width}px`;
      toolbar.style.top = `80px`;
    }
    // If we've scrolled past the parent section, unfix the toolbar
    else if (
      isToolbarFixed.value &&
      scrollY > parentBottom - toolbarPosition.value.height
    ) {
      isToolbarFixed.value = false;
      toolbar.style.top = `0px`;
      toolbar.style.width = "";
    }
  } else {
    // When scrolling back above the original position, always unfix
    if (isToolbarFixed.value) {
      isToolbarFixed.value = false;
      toolbar.style.top = `0px`;
      toolbar.style.width = "";
    }
  }
};

// Initialize scroll and resize listeners
onMounted(() => {
  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("resize", handleResize, { passive: true });

  // Initial position calculation
  nextTick(() => {
    handleScroll();
  });
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", handleResize);
});

// Watch for editor changes and reset the toolbar position
watch(
  () => props.editor,
  () => {
    toolbarPosition.value = { top: 0, originalTop: 0, height: 0 };
    nextTick(() => {
      handleScroll();
    });
  }
);

// Reset toolbar position when window resizes
const handleResize = () => {
  if (isToolbarFixed.value) {
    isToolbarFixed.value = false;
    toolbarRef.value.style.top = `0px`;
    toolbarRef.value.style.width = "";
  }

  // Reset measurements so they'll be recalculated on next scroll
  toolbarPosition.value = {
    top: 0,
    originalTop: 0,
    height: 0,
  };

  // Force recalculation on next scroll
  nextTick(() => {
    handleScroll();
  });
};

// Modal control methods
const openImageModal = () => {
  showImageModal.value = true;
};

const closeImageModal = () => {
  showImageModal.value = false;
};

const openLinkModal = () => {
  showLinkModal.value = true;
};

const closeLinkModal = () => {
  showLinkModal.value = false;
};

const openYoutubeModal = () => {
  showYoutubeModal.value = true;
};

const closeYoutubeModal = () => {
  showYoutubeModal.value = false;
};

const openTooltipModal = (iconType) => {
  tooltipIconType.value = iconType;
  showTooltipModal.value = true;
};

const closeTooltipModal = () => {
  showTooltipModal.value = false;
};

const openCellBgColorModal = () => {
  showCellBgColorModal.value = true;
};

const closeCellBgColorModal = () => {
  showCellBgColorModal.value = false;
};
</script>

<style scoped>
.toolbar-wrapper {
  position: relative;
}

.tiptap-toolbar-container {
  position: relative;
  z-index: 10;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px 4px 0 0;
  border-bottom: none;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  width: 100%;
}

.tiptap-toolbar-container.is-fixed {
  position: fixed;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateZ(0);
  will-change: transform;
}

/* Placeholder to prevent content jumping when toolbar becomes fixed */
.toolbar-placeholder {
  width: 100%;
  visibility: hidden;
}

.tiptap-toolbar {
  padding: 8px 10px;
  background-color: #f8f9fa;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.tiptap-toolbar button {
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 5px 7px;
  cursor: pointer;
  font-size: 0.85em;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
}

.tiptap-toolbar button:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.tiptap-toolbar button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tiptap-toolbar button.is-active {
  background-color: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

/* Divider for visual separation */
.divider {
  width: 1px;
  background-color: #ced4da;
  margin: 4px 8px;
  align-self: stretch;
}
</style>

<style>
/* Table Styles */
.default-table {
  border-collapse: collapse;
  width: 100%;
}
.default-table th,
.default-table td {
  border: 1px solid #dee2e6 !important;
}

/* Normalize font properties for table cells */
.default-table td {
  font-size: inherit !important;
  font-weight: normal !important;
  line-height: 1.6 !important;
  font-family: inherit !important;
}

.borderless-table {
  border-collapse: collapse;
  width: 100%;
}
.borderless-table th,
.borderless-table td {
  border: none !important;
  border-bottom: 1px solid #f1f3f5 !important;
}

/* Normalize font properties for borderless table cells */
.borderless-table td {
  font-size: inherit !important;
  font-weight: normal !important;
  line-height: 1.6 !important;
  font-family: inherit !important;
}

/* Override default table styles from EditorSection.vue */
.tiptap-wrapper .ProseMirror table {
  border: 1px solid #dee2e6;
}

.tiptap-wrapper .ProseMirror table.borderless-table {
  border: none;
}
</style>
